import React from 'react';
import {
  MODAL_PANEL_FLEX_SHELL,
  MODAL_PANEL_MAX_HEIGHT,
  MODAL_PANEL_PADDING,
  MODAL_PANEL_RADIUS,
  MODAL_PANEL_SCROLL_BODY,
  MODAL_PANEL_WIDTH,
} from './modalStyles';

/**
 * ModalContent component for consistent modal content styling.
 * Provides size variants, mobile-safe max height, and inset panel sizing.
 *
 * @param {boolean} [padding=true] - When false, omit default padding (e.g. full-bleed header/footer).
 * @param {boolean} [constrainHeight=true] - When false, omit default max-height (for short dialogs).
 * @param {boolean} [scrollBody] - When true, wrap children in a scrollable body. Defaults to
 *   `padding && constrainHeight`. Set false when the modal manages its own header/body scroll layout.
 */
const ModalContent = ({
  children,
  size = 'medium',
  className = '',
  padding = true,
  constrainHeight = true,
  scrollBody,
  ...props
}) => {
  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-md sm:max-w-2xl',
    large: 'max-w-md sm:max-w-4xl',
    xlarge: 'max-w-lg sm:max-w-6xl',
    full: 'max-w-full',
  };

  const useScrollBody = scrollBody ?? (padding && constrainHeight);
  const userManagesScrollLayout =
    !padding &&
    constrainHeight &&
    /\b(?:overflow-hidden|flex(?:\s|$))\b/.test(className);
  const useFlexShell = constrainHeight && (useScrollBody || userManagesScrollLayout);

  const panelClasses = [
    'relative bg-surface shadow-lg',
    MODAL_PANEL_WIDTH,
    MODAL_PANEL_RADIUS,
    constrainHeight ? MODAL_PANEL_MAX_HEIGHT : '',
    !useScrollBody && padding ? MODAL_PANEL_PADDING : '',
    sizeClasses[size] ?? sizeClasses.medium,
    'overscroll-contain',
    className,
    useFlexShell ? MODAL_PANEL_FLEX_SHELL : '',
    constrainHeight && !useFlexShell ? 'min-h-0 overflow-y-auto' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={panelClasses}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      {useScrollBody ? (
        <div className={[MODAL_PANEL_SCROLL_BODY, MODAL_PANEL_PADDING].join(' ')}>
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default ModalContent;
